import { useLazyQuery, useQuery } from "@apollo/client";
import { gql } from "generated-graphql";
import { GetFacilitiesQuery } from "generated-graphql/graphql";
import invariant from "invariant";
import { uniqBy } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FieldValues, Path, PathValue, UseFormReturn } from "react-hook-form";

import { PickerPopoverBase } from "./PickerPopoverBase";
import { useTaskFormTenantId } from "./TaskFormTenantContext";

type ElementType<V> = V extends (infer U)[] ? U : V;

export type FacilityPickerPopoverProps<T extends FieldValues, K extends Path<T>> = {
  open: boolean;
  onClose: () => void;
  anchorEl: HTMLElement | null;
  selectedIds: string[];
  form: UseFormReturn<T>;
  allowMultiselect?: boolean;
  formFieldName: K;
  mapValue?: (facility: { id: string }) => ElementType<PathValue<T, K>>;
};

const FACILITIES_QUERY = gql(`
  query GetFacilities($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    facilities(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        name
        customerFacilityId
      }
    }
  }
`);

type FacilityItem = NonNullable<NonNullable<GetFacilitiesQuery["facilities"]>["items"]>[number];

export const FacilityPickerPopover = <T extends FieldValues, K extends Path<T>>({
  open,
  onClose,
  anchorEl,
  selectedIds,
  allowMultiselect = false,
  form,
  formFieldName,
  mapValue,
}: FacilityPickerPopoverProps<T, K>) => {
  const tenantId = useTaskFormTenantId();
  invariant(tenantId, "tenantId is required");

  const PAGE_SIZE = 10;
  const [search, setSearch] = useState("");
  const [selectedFacilityItems, setSelectedFacilityItems] = useState<FacilityItem[]>([]);
  const [loadedItems, setLoadedItems] = useState<FacilityItem[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const pageRef = useRef(0);

  const [loadFacilities] = useLazyQuery(FACILITIES_QUERY);

  useEffect(() => {
    if (selectedIds) {
      loadFacilities({
        variables: {
          search: `tenantId:${tenantId} ids:${selectedIds.join("|")}`,
          sort: [{ field: "name", sort: "asc" }],
        },
      }).then(({ data }) => {
        setSelectedFacilityItems(data?.facilities?.items ?? []);
      });
    }
  }, [selectedIds, loadFacilities, tenantId]);

  const { data, previousData, loading, fetchMore } = useQuery(FACILITIES_QUERY, {
    variables: {
      search: `tenantId:${tenantId} ${search ?? ""}`,
      page: 0,
      pageSize: PAGE_SIZE,
      sort: [{ field: "name", sort: "asc" }],
    },
    skip: !open,
    onCompleted: (data) => {
      const items = data?.facilities?.items ?? [];
      setLoadedItems(items);
      setHasMore(items.length >= PAGE_SIZE);
      pageRef.current = 0;
    },
  });

  // Reset pagination when search changes
  useEffect(() => {
    pageRef.current = 0;
    setLoadedItems([]);
    setHasMore(true);
  }, [search]);

  const handleLoadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    const nextPage = pageRef.current + 1;
    pageRef.current = nextPage;

    try {
      const { data: moreData } = await fetchMore({
        variables: {
          search: `tenantId:${tenantId} ${search ?? ""}`,
          page: nextPage,
          pageSize: PAGE_SIZE,
          sort: [{ field: "name", sort: "asc" }],
        },
      });
      const newItems = moreData?.facilities?.items ?? [];
      setLoadedItems((prev) => uniqBy([...prev, ...newItems], "id"));
      setHasMore(newItems.length >= PAGE_SIZE);
    } finally {
      setLoadingMore(false);
    }
  }, [fetchMore, hasMore, loadingMore, search, tenantId]);

  const facilityItems = useMemo(
    () =>
      loadedItems.length > 0
        ? loadedItems
        : (data?.facilities?.items ?? previousData?.facilities?.items ?? []),
    [loadedItems, data?.facilities?.items, previousData?.facilities?.items]
  );

  const allFacilityItems = useMemo(
    () => uniqBy([...selectedFacilityItems, ...facilityItems], "id"),
    [selectedFacilityItems, facilityItems]
  );

  return (
    <PickerPopoverBase
      open={open}
      onClose={onClose}
      anchorEl={anchorEl}
      search={search}
      onSearchChange={setSearch}
      loading={loading}
      items={allFacilityItems}
      selectedIds={selectedIds}
      allowMultiselect={allowMultiselect}
      renderPrimary={(item: (typeof facilityItems)[number]) =>
        `${item.name}${item.customerFacilityId ? ` (${item.customerFacilityId})` : ""}`
      }
      emptyLabel="No facilities found"
      form={form}
      formFieldName={formFieldName}
      mapValue={mapValue}
      onLoadMore={handleLoadMore}
      hasMore={hasMore}
      loadingMore={loadingMore}
    />
  );
};
